import { MoreHoriz, OpenInNew, Settings } from '@mui/icons-material'
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogTitle,
  IconButton,
  Menu,
  MenuItem,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  Typography,
} from '@mui/material'
import type {
  Improve,
  ImproveSortColumnEnum,
  ImproveStatusStatusEnum,
  IndicatedIssueGroup,
  SortOrderEnum,
} from '@ulysses-inc/harami_api_client'
import React, { useEffect, useState } from 'react'
import { Link } from 'react-router-dom'
import { Header } from 'src/components/header/Header'
import { HeaderTitle } from 'src/components/header/HeaderTitle'
import {
  getStatusColor,
  getStatusIconComponent,
  getStatusName,
} from 'src/constants/improveStatus'
import { localStorageKeys } from 'src/constants/localStorageKeys'
import date from 'src/exShared/util/date'
import ImprovesFilterDropDown from 'src/features/improves/improveList/ImproveFilterDropDown'
import type { ImprovesProps } from 'src/features/improves/improveList/ImprovesProps'
import ImprovesSortDropDown from 'src/features/improves/improveList/ImprovesSortDropDown'
import type { ImprovesSortOrder } from 'src/features/improves/improveList/improvesSortOrder'
import {
  DEFAULT_IMPROVES_SORT_ORDER,
  improvesSortOrderStorage,
} from 'src/features/improves/improveList/improvesSortOrder'
import { semanticColors } from 'src/features/theme/semanticColors'
import { useListFilter } from 'src/hooks/filter/useListFilter'
import type { GetImprovesFilter } from 'src/state/ducks/improves/types'

const mergeSortIntoFilter = (
  filter: GetImprovesFilter | undefined,
  sortOrder: ImprovesSortOrder | undefined,
): GetImprovesFilter | undefined => {
  const { sortColumn: _, sortOrder: __, ...rest } = filter ?? {}
  if (!sortOrder?.sortColumn) {
    return Object.keys(rest).length > 0 ? rest : undefined
  }
  return {
    ...rest,
    sortColumn: sortOrder.sortColumn,
    sortOrder: sortOrder.sortOrder,
  }
}

const rejectExists = (input: IndicatedIssueGroup[]) => {
  return (input ?? []).some((group: IndicatedIssueGroup) => {
    if (!group.indicatedIssues) {
      return false
    }
    return !!group.indicatedIssues[group.indicatedIssues.length - 1]
      ?.rejectComment?.comment?.length
  })
}

const reviseExists = (input: IndicatedIssueGroup[]) => {
  return (input ?? []).some((group: IndicatedIssueGroup) => {
    if (!group.indicatedIssues) {
      return false
    }
    return !!group.indicatedIssues[group.indicatedIssues.length - 1]?.revise
      ?.comment?.length
  })
}

const StatusCell: React.FC<{
  status?: ImproveStatusStatusEnum
  reject: boolean
  revise: boolean
}> = ({ status, reject, revise }) => {
  if (!status) return null
  const StatusIconComponent = getStatusIconComponent(status)
  if (!StatusIconComponent) return null
  const statusColor = getStatusColor(status)
  const statusName = getStatusName(status, reject, revise)
  return (
    <Stack direction="row" alignItems="center" spacing={0.5}>
      {React.createElement(StatusIconComponent, {
        style: { color: statusColor, fontSize: 14 },
      })}
      <Typography variant="body2" sx={{ color: statusColor }}>
        {statusName}
      </Typography>
    </Stack>
  )
}

type RowData = {
  uuid: string
  reportName?: string
}

const ImprovesScene: React.FC<ImprovesProps> = (props: ImprovesProps) => {
  const { filters, setListFilter } = useListFilter()
  const [improvesSortOrder, setImprovesSortOrder] = useState<ImprovesSortOrder>(
    improvesSortOrderStorage.get() ?? DEFAULT_IMPROVES_SORT_ORDER,
  )

  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null)
  const [selectedRow, setSelectedRow] = useState<RowData | null>(null)
  const [openDeleteDialog, setOpenDeleteDialog] = useState(false)

  const handleMenuClick = (
    event: React.MouseEvent<HTMLElement>,
    row: RowData,
  ) => {
    event.stopPropagation()
    event.preventDefault()
    setAnchorEl(event.currentTarget)
    setSelectedRow(row)
  }

  const handleMenuClose = () => {
    setAnchorEl(null)
  }

  const handleFilterChange = (filter?: GetImprovesFilter) => {
    props.getImproves(
      props.request,
      mergeSortIntoFilter(filter, improvesSortOrder),
    )
  }

  const handleSortChange = (
    isClear: boolean,
    sortColumn: ImproveSortColumnEnum | undefined,
    sortOrder: SortOrderEnum,
  ) => {
    const nextSortOrder: ImprovesSortOrder =
      isClear || !sortColumn
        ? DEFAULT_IMPROVES_SORT_ORDER
        : { sortColumn, sortOrder }
    if (isClear || !sortColumn) {
      improvesSortOrderStorage.remove()
    } else {
      improvesSortOrderStorage.set(nextSortOrder)
    }
    setImprovesSortOrder(nextSortOrder)
    props.getImproves(
      props.request,
      mergeSortIntoFilter(props.filter, nextSortOrder),
    )
  }

  useEffect(() => {
    props.getPlaces()
    props.getPlaceGroups()

    if (filters?.improves) {
      const improvesFilter = filters.improves
      const limit = improvesFilter?.pagination?.limit ?? 25
      const offset = improvesFilter?.pagination?.offset ?? 0
      props.updatePagination(limit, offset)
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  useEffect(() => {
    setListFilter({
      ...(filters ?? {}),
      improves: {
        pagination: props.request,
        filter: filters?.improves?.filter,
      },
    })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [props.request.limit, props.request.offset])

  const loginUserUUID =
    localStorage.getItem(localStorageKeys.loginUserUuid) ?? ''

  // MUI TablePagination は 0-origin
  const limit = props.request.limit ?? 25
  const offset = props.request.offset ?? 0
  const maxPage =
    limit === 0 ? 0 : Math.max(0, Math.ceil(props.count / limit) - 1)
  const currentPage = Math.min(Math.floor(offset / limit), maxPage)

  const handleChangePage = (
    _: React.MouseEvent<HTMLButtonElement> | null,
    newPage: number,
  ) => {
    // changeImprovesPage は 1-origin
    props.changeImprovesPage(
      newPage + 1,
      props.request.limit ?? 25,
      props.filter,
    )
  }

  const handleChangeRowsPerPage = (
    event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
  ) => {
    const size = parseInt(event.target.value, 10)
    props.changeImprovesSize(size, props.filter)
  }

  return (
    <Stack sx={{ height: 'calc(100vh - var(--banner-height, 0px))' }}>
      <Box sx={{ height: 72, flexShrink: 0 }}>
        <Header>
          <HeaderTitle title="カイゼン" />
        </Header>
      </Box>
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          p: 2,
          overflowX: 'auto',
          flexShrink: 0,
        }}
      >
        <Stack direction="row" spacing={2} alignItems="center">
          <ImprovesFilterDropDown
            places={props.places}
            getFilterImproves={handleFilterChange}
            isLoadingPlace={props.isLoadingPlace}
            request={props.request}
          />
          <ImprovesSortDropDown
            getSortedImproves={handleSortChange}
            improvesSortOrder={improvesSortOrder}
          />
        </Stack>
        <Button
          component={Link}
          to="/indicatedIssueLabels"
          variant="outlined"
          color="secondary"
          startIcon={<Settings />}
          sx={{ ml: 'auto' }}
        >
          ラベル設定
        </Button>
      </Box>
      <TableContainer
        sx={{
          overflow: 'auto',
          position: 'relative',
          flexGrow: 1,
          bgcolor: 'white',
        }}
      >
        <Table
          sx={{ bgcolor: 'white', width: '100%', minWidth: 800 }}
          stickyHeader
        >
          <TableHead>
            <TableRow>
              <TableCell size="small" sx={{ width: '35%', bgcolor: 'white' }}>
                レポート名
              </TableCell>
              <TableCell size="small" sx={{ width: '25%', bgcolor: 'white' }}>
                現場名
              </TableCell>
              <TableCell size="small" sx={{ width: '25%', bgcolor: 'white' }}>
                実施日
              </TableCell>
              <TableCell size="small" sx={{ width: '14%', bgcolor: 'white' }}>
                ステータス
              </TableCell>
              <TableCell
                size="small"
                sx={{ bgcolor: 'white' }}
                aria-label="操作"
              />
            </TableRow>
          </TableHead>
          <TableBody>
            {props.improves.map((improve: Improve) => (
              <TableRow
                key={improve.uuid}
                component={Link}
                to={`/improves/${improve.uuid}`}
                sx={{
                  cursor: 'pointer',
                  textDecoration: 'none',
                  color: 'inherit',
                  display: 'table-row',
                  '&:hover': {
                    backgroundColor: semanticColors.colorBgTextHover,
                  },
                }}
              >
                <TableCell size="small" sx={{ wordBreak: 'break-word' }}>
                  <Stack direction="row" alignItems="center">
                    <Typography variant="body2">
                      {improve.reportName}
                    </Typography>
                    {!improve.reportDeletedAt && (
                      <IconButton
                        size="small"
                        aria-label="レポート詳細を開く"
                        onClick={(e: React.MouseEvent) => {
                          e.stopPropagation()
                          e.preventDefault()
                          props.goReportDetail({
                            reportUUID: improve.reportUUID,
                          })
                        }}
                        sx={{ ml: 0.5 }}
                      >
                        <OpenInNew
                          sx={{ fontSize: 14, color: 'primary.main' }}
                        />
                      </IconButton>
                    )}
                  </Stack>
                </TableCell>
                <TableCell size="small">
                  <Typography variant="body2">{improve.place?.name}</Typography>
                </TableCell>
                <TableCell size="small">
                  <Typography variant="body2">
                    {date.formatYYYYMMDD(improve.reportCreatedAt)}
                  </Typography>
                </TableCell>
                <TableCell size="small">
                  <StatusCell
                    status={improve.status?.status}
                    reject={rejectExists(
                      improve.indicatedIssueGroups as IndicatedIssueGroup[],
                    )}
                    revise={reviseExists(
                      improve.indicatedIssueGroups as IndicatedIssueGroup[],
                    )}
                  />
                </TableCell>
                <TableCell
                  size="small"
                  align="right"
                  onClick={e => {
                    e.stopPropagation()
                    e.preventDefault()
                  }}
                >
                  {improve.auditUser?.uuid === loginUserUUID && (
                    <IconButton
                      size="small"
                      aria-label="行の操作メニューを開く"
                      onClick={e =>
                        handleMenuClick(e, {
                          uuid: improve.uuid,
                          reportName: improve.reportName,
                        })
                      }
                    >
                      <MoreHoriz />
                    </IconButton>
                  )}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
      <TablePagination
        count={props.count}
        page={currentPage}
        onPageChange={handleChangePage}
        rowsPerPage={props.request.limit ?? 25}
        onRowsPerPageChange={handleChangeRowsPerPage}
        rowsPerPageOptions={[25, 50, 100]}
        labelRowsPerPage="1ページあたり:"
        labelDisplayedRows={({ from, to, count }) =>
          `${from}〜${to} (${count !== -1 ? count : '不明'} 件中)`
        }
        sx={{
          flexShrink: 0,
          '& .MuiTablePagination-selectLabel': { margin: 0 },
          '& .MuiTablePagination-displayedRows': { margin: 0 },
        }}
      />
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleMenuClose}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
      >
        <MenuItem
          onClick={() => {
            handleMenuClose()
            setOpenDeleteDialog(true)
          }}
          sx={{
            color: semanticColors.colorErrorText,
            '&:hover': {
              backgroundColor: semanticColors.colorErrorBgHover,
              color: semanticColors.colorErrorText,
            },
          }}
        >
          削除
        </MenuItem>
      </Menu>
      <Dialog
        open={openDeleteDialog}
        onClose={() => setOpenDeleteDialog(false)}
      >
        <DialogTitle>{`「${selectedRow?.reportName}」の削除を実行しますがよろしいですか？`}</DialogTitle>
        <DialogActions>
          <Button
            color="secondary"
            variant="outlined"
            onClick={() => setOpenDeleteDialog(false)}
          >
            キャンセル
          </Button>
          <Button
            color="error"
            variant="contained"
            onClick={() => {
              if (selectedRow) {
                props.deleteImprove(selectedRow.uuid)
              }
              setOpenDeleteDialog(false)
            }}
          >
            削除
          </Button>
        </DialogActions>
      </Dialog>
    </Stack>
  )
}

export default ImprovesScene
